/**
 *
 * "Creates B.O.M. (Bill of material) table with panel material information in"
 * The table has two parts
 * Main part always view:
 * part has panels and sideliners material numberm, part number abd code
 * Optional additional part:
 * Details of the materials like weight, price and so on
 *
 * @file   BomTable.js
 * @author Lateral
 * @since  2023
 */
import {
  Button,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import { groupBy } from 'common/arrays'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTheme, styled } from '@mui/material/styles'
import BigNumber from 'bignumber.js'
import { DeckMode } from 'common/deckMode'
import { DeckAccordion } from 'pages/deck/common/DeckAccordion'
import { useGetMaterials } from 'hooks/database/useGetMaterials'
import { colours, getColors, isValidHexColor } from 'common/colours'
import { DeckMappDialog } from 'components/DeckMappDialog'
import Swatches from 'components/colorSwatch/Swatches'

const StyledTypographyMaxWidth = styled(Typography)(() => ({
  maxWidth: '200px',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis'
}))

const StyledCell = styled(TableCell)(() => ({
  minWidth: '100px',
  textOverflow: 'ellipsis'
}))

const sideLinerName = 'sideliner'
const panelName = 'panel'
const otherName = 'other'

function BomTable({
  data,
  selected,
  materialSelected,
  onClick,
  deckMode,
  heatMapData,
  showTitle,
  canGenerateColor,
  setDeckRevision,
  onChange,
  fullHeight = false,
  onOtherKitClick,
  otherKitsSelected
}) {
  /**
   *  Generates table for displaying BOM data.
   * 
   * @function
   * @param {object} data - Deck data with panel information
   * @param {string} data.DeckId - ID of the deck
   * @param {object[]} data.kits - Details of the materials used in the deck
   * @param {object[]} data.Panels - Details of the panels in the deck with material 
   * types and positon
   * @param {object[]} data.Sideliners - Details of the sideliners in the deck with material 
   * @param {object} data.Size - Row and column size of the deck
   * @param {string} data.id - Deck ID
   * @param {object[]} selected - Array of selected panel objects
   * @param {object[]} materialSelected - Materials of selected panels
   * @param {Array} onClick - Action on clicking on table
   * @param {string} deckMode - Deck modes:   'default', 'edit', 'maintenance', 'heatmap', 'maintnance', 'pre-maintenance', 'post-maintenance'
   * @param {object[]} heatMapData - Heatmap data of the panels and sideliners in heatmap page

   * @returns {object} - BOM table with material details of panels and sideliners
   */

  const [includeBom, setIncludeBom] = useState(true)

  const sideLiners = useMemo(() => {
    return groupBy('MaterialNumber')(data.SideLiners)
  }, [data.SideLiners, data.SideLiners?.length])

  const panels = useMemo(() => {
    return groupBy('MaterialNumber')(data.Panels)
  }, [data.Panels, data.Panels?.length])

  const kits = useMemo(() => {
    return [...(data.Kits ?? [])].sort((a, b) => a.MaterialNumber.localeCompare(b.MaterialNumber))
  }, [data.Kits, data.Kits?.length])

  const selectedColors = useRef([])

  let counter = 0
  const theme = useTheme()

  const [kitData, setKitData] = useState({
    sideLinerKits: [],
    panelKits: [],
    otherKits: []
  })
  const { materials } = useGetMaterials()

  const [colorDialogData, setColorDialogData] = useState({
    isOpen: false,
    title: '',
    type: '',
    index: -1,
    kit: undefined
  })

  const colorRef = useRef(undefined)

  useEffect(() => {
    const sideLinerKits = kits.filter((k) => panelTypeCheck(k.MaterialNumber) === sideLinerName)
    const panelKits = kits.filter((k) => panelTypeCheck(k.MaterialNumber) === panelName)
    const otherKits = kits.filter((k) => panelTypeCheck(k.MaterialNumber) === otherName)

    setKitData({
      sideLinerKits,
      panelKits,
      otherKits
    })
  }, [kits])

  const sideLinersMaterials = useMemo(() => {
    return kitData.sideLinerKits.map((s) => {
      return s.MaterialNumber
    })
  }, [kitData.sideLinerKits])

  const panelMaterials = useMemo(() => {
    return kitData.panelKits.map((s) => {
      return s.MaterialNumber
    })
  }, [kitData.panelKits])

  const otherMaterials = useMemo(() => {
    return kitData.otherKits.map((s) => {
      return s.MaterialNumber
    })
  }, [kitData.panelKits])

  const tableRef = useRef()

  const onColourChanged = () => {
    const { index: i, kit, type } = colorDialogData
    const color = colorRef.current
    const kitIndex = data.Kits.findIndex(({ MaterialNumber }) => MaterialNumber === kit.MaterialNumber)
    const dataCloned = structuredClone(data)
    setKitData((previous) => {
      const items = previous[type]
      const itemColor = items[i].Colour

      selectedColors.current = selectedColors.current.filter((color) => color !== itemColor)
      items.splice(i, 1, {
        ...kit,
        Colour: color
      })

      return {
        ...previous,
        [type]: [...items]
      }
    })
    dataCloned.Kits.splice(kitIndex, 1, {
      ...data.Kits[kitIndex],
      Colour: color
    })
    setDeckRevision(dataCloned)
    setColorDialogData({
      isOpen: false
    })

    selectedColors.current.push(color)
  }

  function panelTypeCheck(material) {
    const panelKeys = Object.keys(panels)
    const sideLinerKeys = Object.keys(sideLiners)

    if (panelKeys.includes(material)) {
      return panelName
    }

    if (sideLinerKeys.includes(material)) {
      return sideLinerName
    }

    return otherName
  }

  function getWearLife(materialNumber, panelType) {
    let results = []
    if (panelType === panelName) {
      results = heatMapData.Panels.filter((p) => p.MaterialNumber === materialNumber)
    } else if (panelType === sideLinerName) {
      results = heatMapData.SideLiners.filter((s) => s.MaterialNumber === materialNumber)
    } else {
      return 0
    }
    const sum = results.reduce((partialSum, a) => partialSum + a.WearCalculation.toNumber(), 0)

    return new BigNumber(sum).dividedBy(results.length).toFormat(2)
  }

  const getKitColorStyling = (kit) => {
    const color = isValidHexColor(kit.Colour) ? kit.Colour : colours[kit.Colour?.toLowerCase()]
    selectedColors.current.push(color)
    return {
      minWidth: '12px',
      padding: 0,
      backgroundColor: color
    }
  }

  return (
    <DeckAccordion title="B.O.M" onChange={onChange}>
      <Stack sx={{ height: '100%' }}>
        <Stack direction="row" sx={{ alignItems: 'center', height: 'min-content !important' }} mb={1}>
          {showTitle && <Typography variant="h5" component="h5"></Typography>}
          {/* Button to switch for additional optional columns */}
          {deckMode !== DeckMode.heatMap ? (
            <Stack direction="row" sx={{ width: '100%', alignItems: 'center', justifyContent: 'end' }}>
              <Typography variant="h5" component="h5">
                Include B.O.M Items
              </Typography>
              <Switch checked={includeBom} onChange={() => setIncludeBom(!includeBom)} color="secondary" />
            </Stack>
          ) : null}
        </Stack>

        <TableContainer
          sx={{
            maxHeight: fullHeight ? 'auto' : '400px !important',
            overflowY: 'scroll',
            '&::-webkit-scrollbar': {
              width: '2px',
              height: '2px'
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: theme.palette.supporting.dark
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.supporting.pale,
              borderRadius: 2
            }
          }}
          ref={tableRef}>
          <Table stickyHeader sx={{ width: '100%' }}>
            <TableHead>
              <TableRow>
                {/* Main part */}
                <TableCell sx={{ width: '10px', padding: 0 }}></TableCell>
                <TableCell>Item</TableCell>
                <TableCell sx={{ width: '16px' }}>Quantity</TableCell>
                <StyledCell>Material Number</StyledCell>
                <StyledCell>Description</StyledCell>
                <StyledCell>OEM Number</StyledCell>

                <StyledCell>Site Stock Number</StyledCell>
                {deckMode === DeckMode.heatMap ? (
                  <StyledCell>Wear Life</StyledCell>
                ) : includeBom ? (
                  <>
                    {/* optional part */}
                    <StyledCell>Weight</StyledCell>
                    <StyledCell>Open Area</StyledCell>
                    <StyledCell>Working Depth</StyledCell>
                    <StyledCell>Failure Working Depth</StyledCell>
                    <StyledCell>Aperture Width</StyledCell>
                    <StyledCell>Failure Aperture Width</StyledCell>
                  </>
                ) : null}
                <StyledCell>Comments</StyledCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Separate Panels, Sideliners, and Other, to maintain table structure */}
              <TableRow>
                <StyledCell sx={{ px: '1em', py: '0.1em', backgroundColor: theme.palette.primary.light }} colSpan="14">
                  PANELS
                </StyledCell>
              </TableRow>
              {kitData.panelKits.map((kit, i) => {
                const total = panels[kit.MaterialNumber]?.length ?? 0
                const isHighlighted =
                  selected.find((s) => s.MaterialNumber === kit.MaterialNumber) ||
                  materialSelected === kit.MaterialNumber ||
                  otherKitsSelected?.includes(kit.MaterialNumber)
                counter += 1
                return (
                  <TableRow
                    key={`${kit.MaterialNumber}_${i}`}
                    sx={{ backgroundColor: isHighlighted ? '#327DFF' : '' }}
                    onClick={() => onClick(kit, panelMaterials, panelName)}>
                    <TableCell
                      onClick={(e) => {
                        if (!canGenerateColor || !setDeckRevision) {
                          return
                        }
                        e.stopPropagation()
                        colorRef.current = isValidHexColor(kit.Colour) ? kit.Colour : colours[kit.Colour?.toLowerCase()]
                        setColorDialogData({
                          type: 'panelKits',
                          kit,
                          index: i,
                          isOpen: true
                        })
                      }}
                      sx={getKitColorStyling(kit)}
                    />
                    <TableCell>{counter}</TableCell>
                    <TableCell>{total}</TableCell>
                    <StyledCell>
                      <StyledTypographyMaxWidth variant="body2">
                        {kit.MaterialDescription ? kit.MaterialNumber : ''}
                      </StyledTypographyMaxWidth>{' '}
                    </StyledCell>
                    <StyledCell>
                      <StyledTypographyMaxWidth variant="body2">
                        {kit.MaterialDescription ? kit.MaterialDescription : kit.MaterialNumber}
                      </StyledTypographyMaxWidth>
                    </StyledCell>
                    <StyledCell>
                      <StyledTypographyMaxWidth variant="body2">{kit.PartNumber}</StyledTypographyMaxWidth>{' '}
                    </StyledCell>

                    <StyledCell>{kit.CustomerStockCode}</StyledCell>
                    {deckMode === DeckMode.heatMap ? (
                      <StyledCell>{`${getWearLife(kit.MaterialNumber, panelName)} Weeks`}</StyledCell>
                    ) : includeBom ? (
                      <>
                        <StyledCell>{kit.Weight}</StyledCell>
                        <StyledCell>{kit.OpenArea}</StyledCell>
                        <StyledCell>{kit.WorkingDepth}</StyledCell>
                        <StyledCell>
                          {kit.FailureWorkingDepth ||
                            materials.find(({ MaterialNumber }) => MaterialNumber === kit.MaterialNumber)?.SafetyDepth}
                        </StyledCell>
                        <StyledCell>{kit.ApertureWidth}</StyledCell>
                        <StyledCell>{kit.FailureApertureWidth}</StyledCell>
                      </>
                    ) : null}

                    <StyledCell>
                      <StyledTypographyMaxWidth variant="body2">{kit.Comments}</StyledTypographyMaxWidth>
                    </StyledCell>
                  </TableRow>
                )
              })}

              <TableRow>
                <StyledCell sx={{ px: '1em', py: '0.1em', backgroundColor: theme.palette.primary.light }} colSpan="14">
                  SIDELINERS
                </StyledCell>
              </TableRow>
              {kitData.sideLinerKits.map((kit, i) => {
                const total = sideLiners[kit.MaterialNumber]?.length ?? 0
                const isHighlighted =
                  selected.find((s) => s.MaterialNumber === kit.MaterialNumber) ||
                  materialSelected === kit.MaterialNumber ||
                  otherKitsSelected?.includes(kit.MaterialNumber)

                counter += 1
                return (
                  <TableRow
                    key={`${kit.MaterialNumber}_${i}`}
                    sx={{ backgroundColor: isHighlighted ? '#327DFF' : '' }}
                    onClick={() => onClick(kit, sideLinersMaterials, sideLinerName)}>
                    <TableCell
                      onClick={(e) => {
                        if (!canGenerateColor || !setDeckRevision) {
                          return
                        }
                        e.stopPropagation()

                        colorRef.current = isValidHexColor(kit.Colour) ? kit.Colour : colours[kit.Colour?.toLowerCase()]
                        setColorDialogData({
                          type: 'sideLinerKits',
                          kit,
                          index: i,
                          isOpen: true
                        })
                      }}
                      sx={getKitColorStyling(kit)}
                    />
                    <TableCell>{counter}</TableCell>
                    <TableCell>{total}</TableCell>
                    <StyledCell>
                      <StyledTypographyMaxWidth variant="body2">
                        {kit.MaterialDescription ? kit.MaterialNumber : ''}
                      </StyledTypographyMaxWidth>{' '}
                    </StyledCell>
                    <StyledCell>
                      <StyledTypographyMaxWidth variant="body2">
                        {kit.MaterialDescription ? kit.MaterialDescription : kit.MaterialNumber}
                      </StyledTypographyMaxWidth>{' '}
                    </StyledCell>
                    <StyledCell>
                      <StyledTypographyMaxWidth variant="body2">{kit.PartNumber}</StyledTypographyMaxWidth>{' '}
                    </StyledCell>

                    <StyledCell>{kit.CustomerStockCode}</StyledCell>
                    {deckMode === DeckMode.heatMap ? (
                      <StyledCell>{`${getWearLife(kit.MaterialNumber, sideLinerName)} Weeks`}</StyledCell>
                    ) : includeBom ? (
                      <>
                        <StyledCell>{kit.Weight}</StyledCell>
                        <StyledCell>{kit.OpenArea}</StyledCell>
                        <StyledCell>{kit.WorkingDepth}</StyledCell>
                        <StyledCell>{kit.FailureWorkingDepth}</StyledCell>
                        <StyledCell>{kit.ApertureWidth}</StyledCell>
                        <StyledCell>{kit.FailureApertureWidth}</StyledCell>
                      </>
                    ) : null}
                    <TableRow>
                      <StyledCell>
                        <StyledTypographyMaxWidth variant="body2">{kit.Comments}</StyledTypographyMaxWidth>
                      </StyledCell>
                    </TableRow>
                  </TableRow>
                )
              })}

              <TableRow>
                <StyledCell sx={{ px: '1em', py: '0.1em', backgroundColor: theme.palette.primary.light }} colSpan="14">
                  OTHERS
                </StyledCell>
              </TableRow>
              {kitData.otherKits.map((kit, i) => {
                const isHighlighted =
                  selected.find((s) => s.MaterialNumber === kit.MaterialNumber) ||
                  materialSelected === kit.MaterialNumber ||
                  otherKitsSelected?.includes(kit.MaterialNumber)
                counter += 1
                return (
                  <TableRow
                    key={`${kit.MaterialNumber}_${i}`}
                    sx={{ backgroundColor: isHighlighted ? '#327DFF' : '' }}
                    onClick={() => {
                      if (onOtherKitClick) {
                        onOtherKitClick(kit)
                      } else {
                        onClick(kit, otherMaterials, otherName)
                      }
                    }}>
                    <TableCell
                      onClick={(e) => {
                        if (!canGenerateColor || !setDeckRevision) {
                          return
                        }
                        e.stopPropagation()

                        colorRef.current = isValidHexColor(kit.Colour) ? kit.Colour : colours[kit.Colour?.toLowerCase()]
                        setColorDialogData({
                          type: 'otherKits',
                          kit,
                          index: i,
                          isOpen: true
                        })
                      }}
                      sx={getKitColorStyling(kit)}
                    />
                    <TableCell>{counter}</TableCell>
                    <TableCell>{kit.Quantity ?? 0}</TableCell>
                    <StyledCell>
                      <StyledTypographyMaxWidth variant="body2">
                        {kit.MaterialDescription ? kit.MaterialNumber : ''}
                      </StyledTypographyMaxWidth>{' '}
                    </StyledCell>
                    <StyledCell>
                      <StyledTypographyMaxWidth variant="body2">
                        {kit.MaterialDescription ? kit.MaterialDescription : kit.MaterialNumber}
                      </StyledTypographyMaxWidth>{' '}
                    </StyledCell>
                    <StyledCell>
                      <StyledTypographyMaxWidth variant="body2">{kit.PartNumber}</StyledTypographyMaxWidth>{' '}
                    </StyledCell>
                    <StyledCell>{kit.CustomerStockCode}</StyledCell>
                    {deckMode === DeckMode.heatMap ? (
                      <StyledCell>0 Weeks</StyledCell>
                    ) : includeBom ? (
                      <>
                        <StyledCell>{kit.Weight}</StyledCell>
                        <StyledCell>{kit.OpenArea}</StyledCell>
                        <StyledCell>{kit.WorkingDepth}</StyledCell>
                        <StyledCell>{kit.FailureWorkingDepth}</StyledCell>
                        <StyledCell>{kit.ApertureWidth}</StyledCell>
                        <StyledCell>{kit.FailureApertureWidth}</StyledCell>
                      </>
                    ) : null}{' '}
                    <TableRow>
                      <StyledCell>
                        <StyledTypographyMaxWidth variant="body2">{kit.Comments}</StyledTypographyMaxWidth>
                      </StyledCell>
                    </TableRow>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
      <DeckMappDialog
        open={colorDialogData.isOpen}
        title="Change kit color"
        Actions={
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              setColorDialogData({
                isOpen: false
              })
            }}>
            Cancel
          </Button>
        }>
        <Swatches
          onChange={(selectedColor) => {
            colorRef.current = selectedColor.hex
            onColourChanged()
          }}
          color={colorRef.current}
          width="100%"
          colors={getColors(selectedColors.current)}
        />
      </DeckMappDialog>
    </DeckAccordion>
  )
}

export default BomTable
